import * as React from 'react'
import {
  Cpu as CpuIcon,
  Database as DatabaseIcon,
  Plug as PlugIcon,
  Router as RouterIcon
} from 'lucide-react'

/**
 * Canonical keys for `Compute.compute_type`. The enum in the spec is
 * `ignition | aveva | opc | db_server | device | other`
 * (`axilon-apps/docs/scada-ontology-spec.md` §2); we normalize
 * `db_server` to `dbserver` so the key strings are safe to use as
 * object keys and CSS-class fragments. Order is the render order used
 * by the Compute-tab index (Ignition first, Other last). Devices sit
 * after database servers so the Compute tab renders gateways and
 * their backing services before the PLCs / field devices each gateway
 * networks with.
 */
export const COMPUTE_TYPE_KEYS = [
  'ignition',
  'aveva',
  'opc',
  'dbserver',
  'device',
  'other'
] as const
export type ComputeTypeKey = (typeof COMPUTE_TYPE_KEYS)[number]

const COMPUTE_TYPE_KEY_SET: ReadonlySet<string> = new Set(COMPUTE_TYPE_KEYS)

/**
 * Fold an arbitrary `compute_type` string onto one of `COMPUTE_TYPE_KEYS`.
 * Case-insensitive; whitespace / underscores / dashes are stripped so
 * `Db_Server` and `db-server` both normalize to `dbserver`. Anything
 * unrecognized falls back to `other` so the grouping is total.
 */
export function computeTypeKey(computeType: string | undefined): ComputeTypeKey {
  if (!computeType) return 'other'
  const key = computeType.toLowerCase().replace(/[\s_-]/g, '')
  return (COMPUTE_TYPE_KEY_SET.has(key) ? key : 'other') as ComputeTypeKey
}

type IconComponent = React.ComponentType<{ size?: number | string; className?: string }>

const ICONS_BY_KEY: Record<ComputeTypeKey, IconComponent> = {
  ignition: CpuIcon,
  aveva: CpuIcon,
  opc: PlugIcon,
  dbserver: DatabaseIcon,
  device: RouterIcon,
  other: CpuIcon
}

export function computeIconFor(computeType: string | undefined): IconComponent {
  return ICONS_BY_KEY[computeTypeKey(computeType)]
}

const SINGULAR_LABELS: Record<ComputeTypeKey, string> = {
  ignition: 'Ignition Gateway',
  aveva: 'AVEVA Galaxy',
  opc: 'OPC UA Server',
  dbserver: 'Database Server',
  device: 'Device',
  other: 'Compute'
}

const PLURAL_LABELS: Record<ComputeTypeKey, string> = {
  ignition: 'Ignition Gateways',
  aveva: 'AVEVA Galaxies',
  opc: 'OPC UA Servers',
  dbserver: 'Database Servers',
  device: 'Devices',
  other: 'Other Hosts'
}

/**
 * Singular label for a `compute_type`. Returns the raw value verbatim
 * when it's an unrecognized string, so freeform ingest values still
 * surface something usable instead of collapsing to a placeholder.
 */
export function computeLabelFor(computeType: string | undefined): string {
  if (!computeType) return SINGULAR_LABELS.other
  const normalized = computeType.toLowerCase().replace(/[\s_-]/g, '')
  return COMPUTE_TYPE_KEY_SET.has(normalized)
    ? SINGULAR_LABELS[normalized as ComputeTypeKey]
    : computeType
}

/**
 * Plural section heading for a `ComputeTypeKey`. Input is always one of
 * the canonical keys (use `computeTypeKey()` to fold raw values first),
 * so no freeform fallback is needed.
 */
export function computeSectionTitleFor(typeKey: ComputeTypeKey): string {
  return PLURAL_LABELS[typeKey]
}
